import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';
import { Container } from 'reactstrap';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { useHash } from '@whitelabel/helpers/url';
import { IHelpArticle } from '@whitelabel/helpers/types';
import { useQueryParams } from '@whitelabel/helpers/hooks';
import { Loading } from '@whitelabel/component-library';
import { IError, INavItem } from '../../../helpers/types';
import * as actions from '../../../actions';
import Layout from '../../../components/Layout';
import HelpCenterHero from '../../../components/HelpCenterHero';
import NavBar from '../../layout/NavBar';
import PageMeta from '../../helpers/PageMeta';
import ErrorPage from '../ErrorPage';
import Breadcrumb from '../../../components/Breadcrumb';
import QuickActionsFooter from '../../../components/QuickActionsFooter';
import commonMessages from '../../../helpers/messages';
import { StyledSmallContentBlock } from '../../../styled';
import { StyledTitle } from './styled';

interface IHelpProps {
  navItems?: INavItem[];
  locale: string;
  slug: string;
  article?: IHelpArticle;
  loading: boolean;
  error?: IError;
  getHelpArticle: (...args: any[]) => any;
  resetHelpPageError: (...args: any[]) => any;
}

const Help = ({
  navItems = [],
  locale,
  slug,
  article,
  loading,
  error,
  getHelpArticle,
  resetHelpPageError,
}: IHelpProps) => {
  const intl = useIntl();
  const queryParams = useQueryParams();
  const prevTitle = queryParams.get('prevTitle');
  const prevPath = queryParams.get('prevPath');
  const history = useHistory();
  const { pathname } = useLocation();
  const yourInsuranceURL = `/${locale}/help/your-insurance`;

  useEffect(() => {
    const newSlugMapping: Record<string, string> = {
      'help-amazon': 'amazon',
      ryanair: 'ryanair',
      pepperfry: 'pepperfry',
      airbaltic: 'airbaltic',
      'cheers-travel': 'cheers-travel',
      agoda: 'agoda',
      flipkart: 'flipkart',
      'make-my-trip': 'makemytrip',
      rms: 'rms',
      Freightos: 'freightos',
      rhino: 'rhino',
      boxc: 'boxc',
      ola: 'ola',
      skyscanner: 'skyscanner',
      wego: 'wego',
      ebay: 'ebay',
      tbo: 'tbo',
      fnr: 'funraisin',
    };

    if (newSlugMapping[slug]) {
      const hash = history.location.hash;
      const newHash = hash && `#${slug === 'cheers-travel' ? 'cheers' : newSlugMapping[slug]}-${hash.slice(1)}`;
      return history.replace(`/${locale}/help/partners/${newSlugMapping[slug]}${newHash}`);
    }

    resetHelpPageError();
    if (!article) {
      getHelpArticle(slug);
    }
  }, [slug, locale, article, getHelpArticle, resetHelpPageError, history]);

  useHash(loading);

  if (error) {
    return <ErrorPage />;
  }

  if (pathname === yourInsuranceURL) {
    return <Redirect to={`/${locale}/help`} />;
  }

  const breadCrumbItems = [
    {
      name: intl.formatMessage(commonMessages.helpCenter),
      path: '/help',
    },
  ];

  if (prevTitle && prevPath) {
    breadCrumbItems.push({ name: prevTitle, path: prevPath });
  }

  return (
    <Layout navBar={<NavBar items={navItems} size="lg" />}>
      {loading ? (
        <Loading />
      ) : (
        article && (
          <>
            <PageMeta
              title={article.title}
              seoTitle={article.meta.seoTitle}
              searchDescription={article.meta.searchDescription}
              socialTitle={article.meta.socialTitle}
              socialDescription={article.meta.socialDescription}
              socialImage={article.meta.socialImage}
              ogType={article.meta.ogType}
            />
            <HelpCenterHero hidePartnersBlock />
            <Container>
              <Breadcrumb items={breadCrumbItems} />
              <StyledTitle>{article.title}</StyledTitle>
              <StyledSmallContentBlock
                $rtl={intl.bidi}
                blocks={article.body}
                componentSpacing="sm"
                componentSize="small"
              />
              <QuickActionsFooter />
            </Container>
          </>
        )
      )}
    </Layout>
  );
};

const mapStateToProps = (state: any, ownProps: any) => {
  const { locale } = state.intl;
  const { slug } = ownProps.match.params;
  const article = get(`help.data.${locale}.articles.${slug}`, state);
  const { loading, error } = state.help;

  return {
    navItems: state.navs.data.public,
    locale,
    slug,
    article,
    loading,
    error,
  };
};

const mapDispatchToProps = {
  getHelpArticle: actions.getHelpArticle,
  resetHelpPageError: actions.resetHelpPageError,
};

export default connect(mapStateToProps, mapDispatchToProps)(Help);
